/**
 * TanStack Query hooks for the Workflow Designer endpoints under
 * `/api/workflows/admin/`.
 *
 * Gated by the `workflow_designer` feature flag server-side - callers
 * should feature-gate the UI with `<FeatureGate flag="workflow_designer">`
 * before mounting hooks.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import type { PredicateNode } from '@/admin/components/condition-builder/types'

export type WorkflowApplicability = 'CREATION' | 'STATE' | 'GLOBAL'
export type WorkflowActor = 'ADMIN' | 'CONSUMER' | 'SYSTEM'
export type WorkflowTrigger = 'USER' | 'TIMER' | 'WEBHOOK' | 'SYSTEM' | 'EVENT'

export interface WorkflowStateAdmin {
  uuid: string
  key: string
  label: string
  is_terminal: boolean
  is_initial: boolean
  color: string
  role_label: string
  order: number
  side_effects: WorkflowSideEffectEntry[]
}

export interface WorkflowSideEffectEntry {
  handler: string
  config?: Record<string, unknown>
  phase?: string
  failure_policy?: string
  idempotent?: boolean
  when?: PredicateNode | Record<string, never>
}

/**
 * Rewrite §: `overwrites` is a map of entity column → source spec, used
 * by the engine to apply attribute values back onto the bound entity
 * when the action fires. Shape: `{column: {literal: X}}` or
 * `{column: {attribute: "<code_name>"}}`.
 */
export type WorkflowOverwriteSpec =
  | { literal: unknown }
  | { attribute: string }

export interface WorkflowActionAdmin {
  uuid: string
  key: string
  label: string
  icon: string
  applicability: WorkflowApplicability
  actor: WorkflowActor
  trigger: WorkflowTrigger
  order: number
  priority: number
  source_state: string | null
  target_state: string | null
  source_state_key: string | null
  source_state_label: string | null
  source_state_uuid: string | null
  target_state_key: string | null
  target_state_label: string | null
  target_state_uuid: string | null
  condition: PredicateNode | Record<string, never>
  overwrites: Record<string, WorkflowOverwriteSpec>
  required_attrs: string[]
  side_effects: WorkflowSideEffectEntry[]
  ui_hints: Record<string, unknown>
}

export interface WorkflowVersionAdmin {
  uuid: string
  version_number: number
  is_current: boolean
  published_at: string | null
  states: WorkflowStateAdmin[]
  actions: WorkflowActionAdmin[]
}

export interface WorkflowDefinitionAdmin {
  uuid: string
  key: string
  label: string
  description: string
  resource_type: string
  is_active: boolean
  owns_resource_lifecycle: boolean
  metadata: Record<string, unknown>
  current_version: WorkflowVersionAdmin | null
}

export interface ResourceTypeCatalogEntry {
  resource_type: string
  label: string
}

export interface ResourceTypeFieldRef {
  ref: string
  label: string
  type: string
  choices?: string[]
}

export interface EffectHandlerCatalogEntry {
  key: string
  label: string
  description: string
  config_schema: Record<string, unknown>
  default_phase: string
  default_failure_policy: string
  pre_commit_safe: boolean
  supports_dry_run: boolean
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const DEFINITIONS_KEY = (resourceType?: string) =>
  ['workflow-definitions', resourceType ?? 'all'] as const
const DEFINITION_KEY = (uuid: string) =>
  ['workflow-definition', uuid] as const
const RESOURCE_TYPES_KEY = ['workflow-resource-types'] as const
const RESOURCE_TYPE_FIELDS_KEY = (type: string) =>
  ['workflow-resource-type-fields', type] as const
const EFFECT_HANDLERS_KEY = ['workflow-effect-handlers'] as const

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useWorkflowDefinitions(resourceType?: string) {
  return useQuery<WorkflowDefinitionAdmin[]>({
    queryKey: DEFINITIONS_KEY(resourceType),
    queryFn: () =>
      api
        .get<WorkflowDefinitionAdmin[]>('/workflows/admin/definitions/', {
          params: resourceType ? { resource_type: resourceType } : undefined,
        })
        .then((r) => r.data ?? []),
    staleTime: 30_000,
  })
}

export function useWorkflowDetail(uuid: string | undefined) {
  return useQuery<WorkflowDefinitionAdmin>({
    queryKey: DEFINITION_KEY(uuid ?? ''),
    enabled: Boolean(uuid),
    queryFn: () =>
      api
        .get<WorkflowDefinitionAdmin>(`/workflows/admin/definitions/${uuid}/`)
        .then((r) => r.data),
    staleTime: 30_000,
  })
}

export function useResourceTypeCatalog() {
  return useQuery<ResourceTypeCatalogEntry[]>({
    queryKey: RESOURCE_TYPES_KEY,
    queryFn: () =>
      api
        .get<ResourceTypeCatalogEntry[]>('/workflows/admin/resource-types/')
        .then((r) => r.data ?? []),
    staleTime: 5 * 60_000,
  })
}

export function useResourceTypeFields(resourceType: string | undefined) {
  return useQuery<{ resource_type: string; fields: ResourceTypeFieldRef[] }>({
    queryKey: RESOURCE_TYPE_FIELDS_KEY(resourceType ?? ''),
    enabled: Boolean(resourceType),
    queryFn: () =>
      api
        .get<{ resource_type: string; fields: ResourceTypeFieldRef[] }>(
          `/workflows/admin/resource-types/${resourceType}/fields/`,
        )
        .then((r) => r.data),
    staleTime: 5 * 60_000,
  })
}

export function useEffectHandlerCatalog() {
  return useQuery<EffectHandlerCatalogEntry[]>({
    queryKey: EFFECT_HANDLERS_KEY,
    queryFn: () =>
      api
        .get<EffectHandlerCatalogEntry[]>('/workflows/admin/effect-handlers/')
        .then((r) => r.data ?? []),
    staleTime: 5 * 60_000,
  })
}

// ---------------------------------------------------------------------------
// Workflow mutations
// ---------------------------------------------------------------------------

export interface WorkflowCreateInput {
  key: string
  label: string
  description?: string
  owns_resource_lifecycle?: boolean
  metadata?: Record<string, unknown>
}

export function useCreateWorkflow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: WorkflowCreateInput) =>
      api
        .post<WorkflowDefinitionAdmin>('/workflows/admin/definitions/', body)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflow-definitions'] })
    },
  })
}

export interface WorkflowUpdateInput {
  label?: string
  description?: string
  is_active?: boolean
  owns_resource_lifecycle?: boolean
  metadata?: Record<string, unknown>
}

export function useUpdateWorkflow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      uuid,
      ...body
    }: { uuid: string } & WorkflowUpdateInput) =>
      api
        .patch<WorkflowDefinitionAdmin>(
          `/workflows/admin/definitions/${uuid}/`,
          body,
        )
        .then((r) => r.data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['workflow-definitions'] })
      qc.invalidateQueries({ queryKey: DEFINITION_KEY(variables.uuid) })
    },
  })
}

export function useDeleteWorkflow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) =>
      api.delete(`/workflows/admin/definitions/${uuid}/`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflow-definitions'] })
    },
  })
}

// ---------------------------------------------------------------------------
// State mutations
// ---------------------------------------------------------------------------

export interface StateCreateInput {
  key: string
  label: string
  is_initial?: boolean
  is_terminal?: boolean
  color?: string
  order?: number
  side_effects?: WorkflowSideEffectEntry[]
}

export function useCreateState(workflowUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: StateCreateInput) =>
      api
        .post<WorkflowStateAdmin>(
          `/workflows/admin/definitions/${workflowUuid}/states/`,
          body,
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflow-definitions'] })
      if (workflowUuid) {
        qc.invalidateQueries({ queryKey: DEFINITION_KEY(workflowUuid) })
      }
    },
  })
}

export type StateUpdateInput = Partial<StateCreateInput>

export function useUpdateState(workflowUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, ...body }: { uuid: string } & StateUpdateInput) =>
      api
        .patch<WorkflowStateAdmin>(`/workflows/admin/states/${uuid}/`, body)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflow-definitions'] })
      if (workflowUuid) {
        qc.invalidateQueries({ queryKey: DEFINITION_KEY(workflowUuid) })
      }
    },
  })
}

export function useDeleteState(workflowUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) =>
      api.delete(`/workflows/admin/states/${uuid}/`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflow-definitions'] })
      if (workflowUuid) {
        qc.invalidateQueries({ queryKey: DEFINITION_KEY(workflowUuid) })
      }
    },
  })
}

export interface ReorderEntry {
  uuid: string
  order: number
}

export function useReorderStates(workflowUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (entries: ReorderEntry[]) =>
      api
        .post(
          `/workflows/admin/definitions/${workflowUuid}/states/reorder/`,
          entries,
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflow-definitions'] })
      if (workflowUuid) {
        qc.invalidateQueries({ queryKey: DEFINITION_KEY(workflowUuid) })
      }
    },
  })
}

// ---------------------------------------------------------------------------
// Action mutations
// ---------------------------------------------------------------------------

export type ActionCreateInput = Partial<
  Omit<
    WorkflowActionAdmin,
    | 'uuid'
    | 'source_state_key'
    | 'source_state_label'
    | 'source_state_uuid'
    | 'target_state_key'
    | 'target_state_label'
    | 'target_state_uuid'
  >
> & {
  key: string
  label: string
}

export function useCreateAction(workflowUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ActionCreateInput) =>
      api
        .post<WorkflowActionAdmin>(
          `/workflows/admin/definitions/${workflowUuid}/actions/`,
          body,
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflow-definitions'] })
      if (workflowUuid) {
        qc.invalidateQueries({ queryKey: DEFINITION_KEY(workflowUuid) })
      }
    },
  })
}

export type ActionUpdateInput = Partial<ActionCreateInput>

export function useUpdateWorkflowAction(workflowUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, ...body }: { uuid: string } & ActionUpdateInput) =>
      api
        .patch<WorkflowActionAdmin>(`/workflows/admin/actions/${uuid}/`, body)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflow-definitions'] })
      if (workflowUuid) {
        qc.invalidateQueries({ queryKey: DEFINITION_KEY(workflowUuid) })
      }
    },
  })
}

export function useDeleteAction(workflowUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) =>
      api.delete(`/workflows/admin/actions/${uuid}/`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflow-definitions'] })
      if (workflowUuid) {
        qc.invalidateQueries({ queryKey: DEFINITION_KEY(workflowUuid) })
      }
    },
  })
}

export function useReorderActions(workflowUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (entries: ReorderEntry[]) =>
      api
        .post(
          `/workflows/admin/definitions/${workflowUuid}/actions/reorder/`,
          entries,
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflow-definitions'] })
      if (workflowUuid) {
        qc.invalidateQueries({ queryKey: DEFINITION_KEY(workflowUuid) })
      }
    },
  })
}

// ---------------------------------------------------------------------------
// Rewrite §5.5 - WorkflowAttributeDefinition (workflow-scoped EAV schema).
// ---------------------------------------------------------------------------

export type WorkflowAttributeFieldType =
  | 'TEXT'
  | 'RICH_TEXT'
  | 'NUMBER'
  | 'BOOLEAN'
  | 'SELECT'
  | 'MULTI_SELECT'
  | 'DOUBLE_SELECT'
  | 'DATE'
  | 'DATE_TIME'
  | 'DOCUMENT'
  | 'FILE'
  | 'IMAGE'
  | 'VIDEO'
  | 'LINK'
  | 'MONEY'
  | 'ADDRESS'
  | 'PHONE'
  | 'EMAIL'

export interface WorkflowAttributeDefinition {
  uuid: string
  code_name: string
  display_name: string
  description: string
  field_type: WorkflowAttributeFieldType
  required: boolean
  editable: boolean
  configuration_data: Record<string, unknown>
  hint: string | null
  order: number
}

export type WorkflowAttributeCreateInput = Omit<
  WorkflowAttributeDefinition,
  'uuid'
>
export type WorkflowAttributeUpdateInput = Partial<WorkflowAttributeCreateInput>

const WORKFLOW_ATTRIBUTES_KEY = (workflowUuid: string) =>
  ['workflow-attributes', workflowUuid] as const

export function useWorkflowAttributes(workflowUuid: string | undefined) {
  return useQuery<WorkflowAttributeDefinition[]>({
    queryKey: WORKFLOW_ATTRIBUTES_KEY(workflowUuid ?? ''),
    enabled: Boolean(workflowUuid),
    queryFn: () =>
      api
        .get<
          WorkflowAttributeDefinition[]
        >(`/workflows/admin/definitions/${workflowUuid}/attributes/`)
        .then((r) => r.data ?? []),
    staleTime: 30_000,
  })
}

export function useCreateWorkflowAttribute(workflowUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: WorkflowAttributeCreateInput) =>
      api
        .post<WorkflowAttributeDefinition>(
          `/workflows/admin/definitions/${workflowUuid}/attributes/`,
          body,
        )
        .then((r) => r.data),
    onSuccess: () => {
      if (workflowUuid) {
        qc.invalidateQueries({ queryKey: WORKFLOW_ATTRIBUTES_KEY(workflowUuid) })
      }
    },
  })
}

export function useUpdateWorkflowAttribute(workflowUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      uuid,
      ...body
    }: { uuid: string } & WorkflowAttributeUpdateInput) =>
      api
        .patch<WorkflowAttributeDefinition>(
          `/workflows/admin/attributes/${uuid}/`,
          body,
        )
        .then((r) => r.data),
    onSuccess: () => {
      if (workflowUuid) {
        qc.invalidateQueries({ queryKey: WORKFLOW_ATTRIBUTES_KEY(workflowUuid) })
      }
    },
  })
}

export function useDeleteWorkflowAttribute(workflowUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) =>
      api.delete(`/workflows/admin/attributes/${uuid}/`).then((r) => r.data),
    onSuccess: () => {
      if (workflowUuid) {
        qc.invalidateQueries({ queryKey: WORKFLOW_ATTRIBUTES_KEY(workflowUuid) })
      }
    },
  })
}

// ---------------------------------------------------------------------------
// WorkflowStateCheck - unified state ENTRY + EXIT checks. ENTRY checks gate
// state entry (MOVE/NONE); EXIT checks gate leaving a state and can DIVERT a
// user action to a different action (e.g. LOA re-routes an over-threshold
// approve to "Send for Approval").
// ---------------------------------------------------------------------------

export type WorkflowRuleSeverity = 'BLOCK' | 'WARN' | 'INFO'
export type WorkflowRuleOutcome = 'MOVE' | 'DIVERT' | 'NONE'
export type WorkflowStateCheckStage = 'ENTRY' | 'EXIT'

export interface WorkflowStateCheck {
  uuid: string
  name: string
  description: string
  conditions: PredicateNode | Record<string, never>
  severity: WorkflowRuleSeverity
  message: string | null
  is_active: boolean
  priority: number
  stage: WorkflowStateCheckStage
  outcome: WorkflowRuleOutcome
  target_state: string | null
  divert_to_action: string | null
  applies_to_actions: string[]
  workflow_state: string
  issue_title: string
  issue_description: string
}

export type WorkflowStateCheckCreateInput = Omit<
  WorkflowStateCheck,
  'uuid' | 'workflow_state'
>
export type WorkflowStateCheckUpdateInput =
  Partial<WorkflowStateCheckCreateInput>

const STATE_CHECKS_KEY = (stateUuid: string) =>
  ['workflow-state-checks', stateUuid] as const

export function useWorkflowStateChecks(stateUuid: string | undefined) {
  return useQuery<WorkflowStateCheck[]>({
    queryKey: STATE_CHECKS_KEY(stateUuid ?? ''),
    enabled: Boolean(stateUuid),
    queryFn: () =>
      api
        .get<
          WorkflowStateCheck[]
        >(`/workflows/admin/states/${stateUuid}/state-checks/`)
        .then((r) => r.data ?? []),
    staleTime: 30_000,
  })
}

export function useCreateStateCheck(stateUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: WorkflowStateCheckCreateInput) =>
      api
        .post<WorkflowStateCheck>(
          `/workflows/admin/states/${stateUuid}/state-checks/`,
          body,
        )
        .then((r) => r.data),
    onSuccess: () => {
      if (stateUuid) {
        qc.invalidateQueries({ queryKey: STATE_CHECKS_KEY(stateUuid) })
      }
    },
  })
}

export function useUpdateStateCheck(stateUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      uuid,
      ...body
    }: { uuid: string } & WorkflowStateCheckUpdateInput) =>
      api
        .patch<WorkflowStateCheck>(
          `/workflows/admin/state-checks/${uuid}/`,
          body,
        )
        .then((r) => r.data),
    onSuccess: () => {
      if (stateUuid) {
        qc.invalidateQueries({ queryKey: STATE_CHECKS_KEY(stateUuid) })
      }
    },
  })
}

export function useDeleteStateCheck(stateUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) =>
      api
        .delete(`/workflows/admin/state-checks/${uuid}/`)
        .then((r) => r.data),
    onSuccess: () => {
      if (stateUuid) {
        qc.invalidateQueries({ queryKey: STATE_CHECKS_KEY(stateUuid) })
      }
    },
  })
}
